import Dialog from '@/components/Dialog';
import { useManageStaffStore } from '../../store/useManageStaffStore';
import Button from '@/components/Button';
import Pagination from '@/components/Pagination';
import { RowData, useImportStaff } from '../../hooks/useImportStaff';
import { Input } from '@/components/ui/input';
import { Plus, Trash2 } from 'lucide-react';

export const ImportDialog = ({
  open,
  setOpen,
  refetch,
}: {
  open?: boolean;
  setOpen: React.Dispatch<boolean>;
  refetch: () => void;
}) => {
  const dialogData = useManageStaffStore((state) => state?.page?.staffImportFromSpreadsheetDialog);
  const {
    fileInput,
    handleFileUpload,
    handleImportStaff,
    handlePageChange,
    isLoading,
    loadingImport,
    paginatedData,
    currentPage,
    hasValidData,
    totalPages,
    addRow,
    removeRow,
    updateRow,
  } = useImportStaff({ refetch, setOpen, rowsPerPage: 10 });

  return (
    <>
      <input type="file" accept=".xlsx,.xls,.csv" ref={fileInput} onChange={handleFileUpload} className="mb-4 hidden" />
      <Dialog
        open={open}
        setOpen={setOpen}
        title={dialogData?.title || ''}
        body={
          <>
            <p className="mb-4 text-left text-sh-16-24">{dialogData?.description || ''}</p>
            <table className="w-full table-auto">
              <thead>
                <tr>
                  <th className="border border-primary-300 bg-white px-4 py-2 text-left">Surname</th>
                  <th className="border border-primary-300 bg-white px-4 py-2 text-left">Forename</th>
                  <th className="border border-primary-300 bg-white px-4 py-2 text-left">Email</th>
                  <th className="w-12 border border-primary-300 bg-white px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, index) => (
                  <tr key={index}>
                    <td className="border p-1">
                      <Input
                        theme="new"
                        value={row.surname}
                        onChange={(e) => updateRow(index, 'surname' as keyof RowData, e.target.value)}
                        placeholder="Surname"
                        className="m-0 h-9"
                      />
                    </td>
                    <td className="border p-1">
                      <Input
                        theme="new"
                        value={row.forename}
                        onChange={(e) => updateRow(index, 'forename' as keyof RowData, e.target.value)}
                        placeholder="Forename"
                        className="m-0 h-9"
                      />
                    </td>
                    <td className="border p-1">
                      <Input
                        theme="new"
                        type="email"
                        value={row.email}
                        onChange={(e) => updateRow(index, 'email' as keyof RowData, e.target.value)}
                        placeholder="Email"
                        className="m-0 h-9"
                      />
                    </td>
                    <td className="border p-1 text-center">
                      <button
                        type="button"
                        onClick={() => removeRow(index)}
                        className="rounded p-1 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700">
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
                {paginatedData.length === 0 && (
                  <tr>
                    <td colSpan={4} className="border px-4 py-8 text-center text-gray-500">
                      No records added. Click Add to insert a new record or upload a file.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                pageSize={totalPages}
                onChangePage={(page: number) => {
                  handlePageChange(page);
                }}
              />
            )}
            <div className="mt-5 flex flex-wrap justify-between gap-4">
              <div className="flex gap-2">
                <Button theme="outline" onClick={addRow} disabled={loadingImport}>
                  <Plus size={18} />
                  Add
                </Button>
                <Button
                  theme="outline"
                  onClick={() => fileInput.current?.click()}
                  loading={isLoading}
                  disabled={loadingImport}>
                  {dialogData?.uploadFileButtonText || 'Upload File'}
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  theme="solid"
                  onClick={() => handleImportStaff()}
                  loading={isLoading || loadingImport}
                  disabled={!hasValidData || loadingImport}>
                  {dialogData?.importStaffButtonText || 'Import Staff'}
                </Button>
                <Button theme="outline" disabled={loadingImport} onClick={() => setOpen(false)}>
                  {dialogData?.closeButtonText || 'Close'}
                </Button>
              </div>
            </div>
          </>
        }
      />
    </>
  );
};
